'use client';

import { jsxs, jsx } from "react/jsx-runtime";
import { memo, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useId } from "../../utils/hooks/useId.js";
import { classNames } from "../../utils/styles/classNames.js";
import { Text } from "../../atoms/Text/Text.js";
import { cloneWithProps } from "../../utils/props/cloneWithProps.js";
import { popover, hidden, portal, wrapper } from "./Popover.css.js";
import { useFloatingUi } from "./utils/useFloatingUi.js";
function PopoverComponent({
  a11yTitle,
  title,
  titleAs,
  hiddenTitle,
  initialFocusRef: initialFocusRefProp,
  trigger: triggerProp,
  triggerRef: triggerRefProp,
  dataQa,
  children,
  defaultPressed
}) {
  const [isOpen, setIsOpen] = useState(!!defaultPressed);
  const id = useId();
  const labelId = `chalk-popover-label-id-${id}`;
  const popoverRef = useRef(null);
  const triggerRef = triggerRefProp || useRef(null);
  const initialFocusRef = initialFocusRefProp || useRef(null);
  const {
    popoverProps: { wrapperRef, ...popoverProps },
    triggerProps: { ...triggerProps }
  } = useFloatingUi({
    isOpen,
    setIsOpen,
    triggerRef
  });
  const handleToggleActions = () => {
    setIsOpen((prev) => !prev);
  };
  useEffect(() => {
    const focusRef = initialFocusRef || popoverRef;
    const handleKeyDown = (event) => {
      var _a;
      if (event.key === "Escape") {
        event.preventDefault();
        handleToggleActions();
        (_a = triggerRef == null ? void 0 : triggerRef.current) == null ? void 0 : _a.focus();
      }
    };
    if (isOpen && window) {
      requestAnimationFrame(() => {
        var _a;
        return (_a = focusRef == null ? void 0 : focusRef.current) == null ? void 0 : _a.focus();
      });
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      var _a;
      if (isOpen && window) {
        window.removeEventListener("keydown", handleKeyDown);
      }
      (_a = triggerRef == null ? void 0 : triggerRef.current) == null ? void 0 : _a.focus();
    };
  }, [isOpen, initialFocusRef, popoverRef]);
  const handleClick = useCallback(
    (event) => {
      event.preventDefault();
      handleToggleActions();
    },
    [triggerProp]
  );
  const triggerCommonProps = {
    ...triggerProps,
    onClick: handleClick,
    "data-qa": dataQa,
    "data-pressed": isOpen,
    "aria-haspopup": "dialog",
    "aria-expanded": isOpen ? "true" : "false",
    pressed: isOpen ? "true" : void 0
  };
  const trigger = cloneWithProps(triggerProp, triggerCommonProps);
  const defaultTitleAs = titleAs || "h3";
  return /* @__PURE__ */ jsxs("div", { className: classNames(wrapper), children: [
    trigger,
    createPortal(
      /* @__PURE__ */ jsx(
        "div",
        {
          className: classNames(portal, !isOpen && hidden),
          ref: wrapperRef,
          ...popoverProps,
          children: /* @__PURE__ */ jsxs(
            "div",
            {
              "aria-labelledby": labelId,
              "aria-modal": "false",
              className: popover,
              ref: popoverRef,
              role: "dialog",
              tabIndex: -1,
              children: [
                /* @__PURE__ */ jsx(
                  Text,
                  {
                    "aria-hidden": a11yTitle ? "true" : void 0,
                    as: a11yTitle ? "span" : defaultTitleAs,
                    color: "onLight.text.primary",
                    id: labelId,
                    variant: "display.sm",
                    visuallyHidden: hiddenTitle,
                    children: title
                  }
                ),
                a11yTitle && /* @__PURE__ */ jsx(Text, { as: defaultTitleAs, visuallyHidden: true, children: a11yTitle }),
                isOpen && children
              ]
            }
          )
        }
      ),
      document.body
    )
  ] });
}
const Popover = /* @__PURE__ */ memo(PopoverComponent);
export {
  Popover
};
